'use client'

import React, { useState, useEffect } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { calculate, type JsonEngineConfig, type JsonEngineInput, type JsonEngineOutput } from '@/core/engines/json'
import TextCaseConverterWidget from './tools/TextCaseConverterWidget'
import BMICalculatorWidget from './tools/BMICalculatorWidget'
import SiteCheckWidget from './tools/SiteCheckWidget'
import { WIDGET_FOOTER_BTN, IconSuggest, IconDownload, IconEmbed, useIsPopup } from './tools/widgetFooterUi'
import CountdownTimerWidget from './tools/CountdownTimerWidget'
import StopwatchWidget from './tools/StopwatchWidget'
import CurrencyConverterWidget from './tools/CurrencyConverterWidget'
import SudokuPuzzleGeneratorWidget from './tools/SudokuPuzzleGeneratorWidget'
import SudokuFlagsWidget from './tools/SudokuFlagsWidget'
import TrigWaveChart from './tools/TrigWaveChart'
import UnitCircleChart from './tools/UnitCircleChart'
import ElementCompositionChart from './tools/ElementCompositionChart'
import IdealGasLawChart from './tools/IdealGasLawChart'
import PhScaleChart from './tools/PhScaleChart'
import ProjectileTrajectoryChart from './tools/ProjectileTrajectoryChart'
import DisplacementTimeChart from './tools/DisplacementTimeChart'
import VelocityTimeChart from './tools/VelocityTimeChart'
import InclineDiagramChart from './tools/InclineDiagramChart'
import SpringForceChart from './tools/SpringForceChart'
import ForceAreaChart from './tools/ForceAreaChart'
import EnergyConservationChart from './tools/EnergyConservationChart'
import HeatTemperatureChart from './tools/HeatTemperatureChart'
import EscapeVelocityChart from './tools/EscapeVelocityChart'
import WindPowerChart from './tools/WindPowerChart'
import WaveformChart from './tools/WaveformChart'
import StandingWaveChart from './tools/StandingWaveChart'
import BuoyancyChart from './tools/BuoyancyChart'
import BoyleLawChart from './tools/BoyleLawChart'
import CubeChart from './tools/CubeChart'
import RectangularPrismChart from './tools/RectangularPrismChart'
import CylinderChart from './tools/CylinderChart'
import ConeChart from './tools/ConeChart'
import SphereChart from './tools/SphereChart'
import SquarePyramidChart from './tools/SquarePyramidChart'
import TriangularPrismChart from './tools/TriangularPrismChart'
import HemisphereChart from './tools/HemisphereChart'
import ConeFrustumChart from './tools/ConeFrustumChart'
import EllipsoidChart from './tools/EllipsoidChart'
import CapsuleChart from './tools/CapsuleChart'
import TorusChart from './tools/TorusChart'
import TriangleChart from './tools/TriangleChart'
import RightTriangleChart from './tools/RightTriangleChart'
import CircleChart from './tools/CircleChart'
import CircleSectorChart from './tools/CircleSectorChart'
import RectangleChart from './tools/RectangleChart'
import SquareChart from './tools/SquareChart'
import ParallelogramChart from './tools/ParallelogramChart'
import TrapezoidChart from './tools/TrapezoidChart'
import RhombusChart from './tools/RhombusChart'
import RegularPolygonChart from './tools/RegularPolygonChart'
import EllipseChart from './tools/EllipseChart'
import KiteChart from './tools/KiteChart'

// Tool IDs that get an inline SVG chart appended to their result box, in
// addition to the normal numeric outputs - the trig calculators' angle
// input is always named angle_value/angle_unit so the chart can read it
// generically without per-tool wiring.
const TRIG_WAVE_CHART_TOOL_IDS: Record<string, 'sin' | 'cos' | 'tan'> = {
    '1384': 'sin',
    '1385': 'cos',
    '1386': 'tan',
}
const UNIT_CIRCLE_CHART_TOOL_ID = '1394'

// Chemistry calculators (category 21) that get an inline SVG chart: the
// composition charts read the raw "formula" input directly, while the gas
// law / pH charts need the *computed* result (since the given/solved
// variable differs per solve_for mode), so those read from `result`.
const CHEMISTRY_COMPOSITION_CHART_TOOL_IDS = ['1396', '1397']
const IDEAL_GAS_LAW_CHART_TOOL_ID = '1403'
const PH_CHART_TOOL_ID = '1404'

// Kinematics calculators (category 45) that get an inline SVG chart. The
// displacement/projectile charts read raw inputs directly (initial_velocity/
// acceleration/time_s, or initial_speed_ms/launch_angle_deg/initial_height_m);
// free fall and the general solver read the *computed* result instead, since
// their raw inputs don't always include a usable time/velocity value.
const DISPLACEMENT_TIME_CHART_TOOL_ID = '1410'
const FREE_FALL_CHART_TOOL_ID = '1413'
const PROJECTILE_CHART_TOOL_ID = '1414'
const VELOCITY_TIME_CHART_TOOL_ID = '1419'

// Dynamics calculators (category 46) that get an inline SVG chart. Impulse
// and Spring Force need the computed result when their force/spring-constant
// value is the solved unknown rather than a raw input; Incline and Work
// always read raw inputs directly.
const IMPULSE_CHART_TOOL_ID = '1422'
const INCLINE_CHART_TOOL_ID = '1425'
const WORK_CHART_TOOL_ID = '1426'
const SPRING_FORCE_CHART_TOOL_ID = '1430'

// Energy calculators (category 47) that get an inline SVG chart. Mechanical
// Energy and Heat Energy read the computed result for whichever value is
// the solved unknown; Escape Velocity and Wind Power always read raw
// inputs directly.
const MECHANICAL_ENERGY_CHART_TOOL_ID = '1432'
const HEAT_ENERGY_CHART_TOOL_ID = '1434'
const ESCAPE_VELOCITY_CHART_TOOL_ID = '1439'
const WIND_POWER_CHART_TOOL_ID = '1442'

// Waves & Matter calculators (category 48) that get an inline SVG chart.
// Wave Speed and Standing Wave read raw inputs directly; Buoyancy and
// Boyle's Law read the computed result when the charted value is the
// solved unknown (Boyle's Law) or always the computed forces (Buoyancy).
const WAVE_SPEED_CHART_TOOL_ID = '1444'
const STANDING_WAVE_CHART_TOOL_ID = '1450'
const BUOYANCY_CHART_TOOL_ID = '1451'
const BOYLE_LAW_CHART_TOOL_ID = '1453'

// Solid Geometry calculators (category 50) - every tool always gets an
// inline SVG diagram of the shape, reading raw inputs directly (no
// solve_for / computed-result ambiguity, unlike earlier batches).
const SOLID_GEOMETRY_CHART_TOOL_IDS: Record<string, string> = {
    '1456': 'cube',
    '1457': 'rectangularPrism',
    '1458': 'cylinder',
    '1459': 'cone',
    '1460': 'sphere',
    '1461': 'squarePyramid',
    '1462': 'triangularPrism',
    '1463': 'hemisphere',
    '1464': 'coneFrustum',
    '1465': 'ellipsoid',
    '1466': 'capsule',
    '1467': 'torus',
}

// Plane Geometry calculators (category 49) - every tool always gets an
// inline SVG diagram of the shape, same reasoning as Solid Geometry above.
const PLANE_GEOMETRY_CHART_TOOL_IDS: Record<string, string> = {
    '1468': 'triangle',
    '1469': 'rightTriangle',
    '1470': 'circle',
    '1471': 'circleSector',
    '1472': 'rectangle',
    '1473': 'square',
    '1474': 'parallelogram',
    '1475': 'trapezoid',
    '1476': 'rhombus',
    '1477': 'regularPolygon',
    '1478': 'ellipse',
    '1479': 'kite',
}

const STEP_BY_STEP_LABEL: Record<string, string> = {
    en: 'Step-by-Step Solution', ru: 'Пошаговое решение', lv: 'Soli pa Solim Risinājums', pl: 'Rozwiązanie Krok po Kroku',
    es: 'Solución Paso a Paso', fr: 'Solution Étape par Étape', it: 'Soluzione Passo dopo Passo', de: 'Schritt-für-Schritt-Lösung',
}

type Props = {
    config: JsonEngineConfig
    interface: any  // Переводы (Labels) из БД
    outputs?: any  // outputs_json из БД: [{name, label, unit, precision}] — для калькуляторов с несколькими результатами
    initialValues?: JsonEngineInput  // Для share links
    h1: string  // Заголовок для header виджета
    lang: string
    toolId?: string  // ID инструмента для аналитики
    h1En?: string  // H1 на английском языке для аналитики
    translations: {
        clear: string
        calculate: string
        result: string
        copy: string
        suggest: string
        getWidget: string
        // Новые переводы для UI
        inputLabel: string
        inputPlaceholder: string
        formatLabel: string
        wordsOption: string
        checkWritingOption: string
        currencyOption: string
        currencyVatOption: string
        letterCaseLabel: string
        lowercaseOption: string
        uppercaseOption: string
        titleCaseOption: string
        sentenceCaseOption: string
        plusVat: string
        downloadWidget: string
        installPrompt: string
    }
    widgetPageSlug?: string  // Slug страницы виджета (id105) - устаревший, используйте toolSlug
    toolSlug?: string  // Slug инструмента для генерации ссылки на виджет
}

export default function CalculatorWidget({
    config,
    interface: ui,
    outputs,
    initialValues,
    h1,
    lang,
    toolId,
    h1En,
    translations,
    widgetPageSlug,
    toolSlug
}: Props) {
    // Для tool_id=1002 используем специальный компонент TextCaseConverterWidget
    if (toolId === '1002' || String(toolId) === '1002') {
        return (
            <TextCaseConverterWidget
                config={config}
                interface={ui}
                initialValues={initialValues}
                h1={h1}
                lang={lang}
                toolId={toolId}
                h1En={h1En}
                translations={translations}
                toolSlug={toolSlug}
            />
        )
    }

    // Для tool_id=1003 используем специальный компонент BMICalculatorWidget
    if (toolId === '1003' || String(toolId) === '1003') {
        return (
            <BMICalculatorWidget
                config={config}
                interface={ui}
                initialValues={initialValues}
                h1={h1}
                lang={lang}
                toolId={toolId}
                h1En={h1En}
                translations={translations}
                toolSlug={toolSlug}
            />
        )
    }
    // Для tool_id=1120 используем специальный компонент CountdownTimerWidget (живой тикающий таймер)
    if (toolId === '1120' || String(toolId) === '1120') {
        return (
            <CountdownTimerWidget
                config={config}
                interface={ui}
                initialValues={initialValues}
                h1={h1}
                lang={lang}
                toolId={toolId}
                h1En={h1En}
                translations={translations}
                toolSlug={toolSlug}
            />
        )
    }

    // Для tool_id=1126 используем специальный компонент StopwatchWidget (живой секундомер)
    if (toolId === '1126' || String(toolId) === '1126') {
        return (
            <StopwatchWidget
                config={config}
                interface={ui}
                initialValues={initialValues}
                h1={h1}
                lang={lang}
                toolId={toolId}
                h1En={h1En}
                translations={translations}
                toolSlug={toolSlug}
            />
        )
    }

    // Для tool_id=1483 используем специальный компонент SiteCheckWidget
    // (пассивная проверка безопасности сайта через серверный роут /api/sitecheck)
    if (toolId === '1483' || String(toolId) === '1483') {
        return (
            <SiteCheckWidget
                h1={h1}
                lang={lang}
                toolId={toolId}
                toolSlug={toolSlug}
                h1En={h1En}
                translations={translations}
            />
        )
    }

    // Для tool_id=1156 используем специальный компонент CurrencyConverterWidget (живые курсы валют)
    if (toolId === '1156' || String(toolId) === '1156') {
        return (
            <CurrencyConverterWidget
                config={config}
                interface={ui}
                initialValues={initialValues}
                h1={h1}
                lang={lang}
                toolId={toolId}
                h1En={h1En}
                translations={translations}
                toolSlug={toolSlug}
            />
        )
    }

    // Для tool_id=1244 используем специальный компонент SudokuPuzzleGeneratorWidget
    // (настоящее интерактивное поле: ввод чисел, проверка, подсказки)
    if (toolId === '1244' || String(toolId) === '1244') {
        return (
            <SudokuPuzzleGeneratorWidget
                config={config}
                interface={ui}
                initialValues={initialValues}
                h1={h1}
                lang={lang}
                toolId={toolId}
                h1En={h1En}
                translations={translations}
                toolSlug={toolSlug}
            />
        )
    }

    // Для tool_id=1482 используем специальный компонент SudokuFlagsWidget
    // (судоку + изучение флагов стран: цифры заменены флагами с анимированным
    // переключением на классический вид, плюс система очков)
    if (toolId === '1482' || String(toolId) === '1482') {
        return (
            <SudokuFlagsWidget
                config={config}
                interface={ui}
                initialValues={initialValues}
                h1={h1}
                lang={lang}
                toolId={toolId}
                h1En={h1En}
                translations={translations}
                toolSlug={toolSlug}
            />
        )
    }

    // Храним значения всех инпутов
    // Инициализируем с default значениями из config
    const getInitialValues = (): JsonEngineInput => {
        const defaults: JsonEngineInput = {}
        const uiInputs = Array.isArray(ui) ? ui : undefined
        const todayStr = new Date().toISOString().slice(0, 10)
        // Поля с defaultBySystem (например, длина в футах/метрах) зависят от текущего
        // значения unit_system - сначала определяем его, прежде чем резолвить остальные default'ы
        const unitSystemFieldConfig = uiInputs?.find((f: any) => f.name === 'unit_system')
        const unitSystemValue =
            unitSystemFieldConfig?.default !== undefined
                ? unitSystemFieldConfig.default
                : config.inputs.find((i) => i.key === 'unit_system')?.default
        config.inputs.forEach((inp) => {
            // Локальный дефолт (например, своя ставка НДС для языка) переопределяет общий config.inputs[].default
            const fieldConfig = uiInputs?.find((f: any) => f.name === inp.key)
            const localeOverride = fieldConfig?.default
            let value: any
            if (fieldConfig?.defaultBySystem && unitSystemValue !== undefined) {
                value = fieldConfig.defaultBySystem[String(unitSystemValue)]
            } else {
                value = localeOverride !== undefined ? localeOverride : inp.default
            }
            // Специальное значение "today" для полей type: 'date' - подставляем реальную
            // текущую дату вместо статичного значения, зафиксированного при сидировании
            if (fieldConfig?.type === 'date' && value === 'today') {
                value = todayStr
            }
            if (value !== undefined) {
                defaults[inp.key] = value
            }
        })

        // Обязательно устанавливаем conversionMode, если его нет
        if (defaults.conversionMode === undefined) {
            defaults.conversionMode = 'words'
        }

        return { ...defaults, ...(initialValues || {}) }
    }
    
    // Инициализируем state с default значениями
    const defaultValues = getInitialValues()
    const [values, setValues] = useState<JsonEngineInput>(defaultValues)
    const [result, setResult] = useState<JsonEngineOutput>({})
    const [copied, setCopied] = useState(false)
    const [deferredPrompt, setDeferredPrompt] = useState<any>(null)
    const [isInstallable, setIsInstallable] = useState(false)
    const isPopup = useIsPopup()

    // Применяем query params из URL (клиент-side)
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const params = new URLSearchParams(window.location.search)
            const urlValues: JsonEngineInput = {}
            
            config.inputs.forEach((inp) => {
                const paramValue = params.get(inp.key)
                if (paramValue) {
                    urlValues[inp.key] = paramValue
                }
            })

            if (Object.keys(urlValues).length > 0) {
                setValues((prev) => {
                    const newValues = { ...prev, ...urlValues }
                    // Гарантируем, что conversionMode всегда есть
                    if (newValues.conversionMode === undefined || newValues.conversionMode === null || newValues.conversionMode === '') {
                        newValues.conversionMode = prev.conversionMode || config.inputs.find(i => i.key === 'conversionMode')?.default || 'words'
                    }
                    // Автоматически считаем при загрузке с query params
                    const calculated = calculate(config, newValues)
                    setResult(calculated)
                    return newValues
                })
            }
        }
    }, [config])

    // Отслеживание загрузки виджета в Google Analytics
    useEffect(() => {
        if (typeof window !== 'undefined') {
            // Получаем referrer (URL страницы, с которой открыт виджет)
            const referrer = document.referrer || window.location.href
            
            // Отправляем событие в Google Analytics через dataLayer
            if ((window as any).dataLayer) {
                (window as any).dataLayer.push({
                    event: 'widget_load',
                    eventCategory: 'Widget',
                    eventAction: 'Widget Loaded',
                    eventLabel: h1 || 'Unknown Widget',
                    widgetToolId: toolId || 'unknown',
                    widgetH1: h1 || 'Unknown',
                    widgetH1En: h1En || h1 || 'Unknown',
                    widgetLang: lang,
                    widgetReferrer: referrer,
                    widgetUrl: window.location.href
                })
            }
        }
    }, [toolId, h1, h1En, lang])

    // PWA Install Prompt - обработка beforeinstallprompt
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const handleBeforeInstallPrompt = (e: Event) => {
                e.preventDefault()
                console.log('beforeinstallprompt event fired')
                setDeferredPrompt(e)
                setIsInstallable(true)
            }

            // Проверяем, может ли страница быть установлена
            if ((window as any).navigator && (window as any).navigator.standalone) {
                // iOS - уже установлено
                console.log('PWA already installed (iOS standalone mode)')
            } else if (window.matchMedia('(display-mode: standalone)').matches) {
                // Уже установлено в standalone режиме
                console.log('PWA already installed (standalone mode)')
            } else {
                // Слушаем событие beforeinstallprompt
                window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
                console.log('Waiting for beforeinstallprompt event...')
            }

            return () => {
                window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
            }
        }
    }, [])

    // Обработка ввода
    const handleChange = (key: string, val: string | number) => {
        // Используем функциональное обновление для немедленного применения изменений
        setValues((prev) => {
            const next = { ...prev, [key]: val }
            // Поля с defaultBySystem (например, длина в футах/метрах) при смене
            // системы единиц (imperial/metric) сбрасываются на разумный default для
            // новой системы, а не остаются в старых единицах под новым лейблом
            const uiInputs = Array.isArray(ui) ? ui : undefined
            uiInputs?.forEach((f: any) => {
                if (f.unitFrom === key && f.defaultBySystem && f.defaultBySystem[String(val)] !== undefined) {
                    next[f.name] = f.defaultBySystem[String(val)]
                }
            })
            return next
        })
    }
    
    // Обработка изменения radio button - упрощенная функция без useCallback
    const handleRadioChange = (value: string) => {
        setValues((prev) => ({
            ...prev,
            conversionMode: value
        }))
    }

    // Главная функция расчета через единый движок
    const handleCalculate = () => {
        const cleanedValues = { ...values }
        // Удаляем пробелы из inputNumber перед расчетом (только для number-to-words)
        if (cleanedValues.inputNumber) {
            cleanedValues.inputNumber = String(cleanedValues.inputNumber).replace(/\s/g, '')
            setValues(cleanedValues)
        }
        const calculated = calculate(config, cleanedValues)
        setResult(calculated)
    }

    // Очистка формы
    const handleClear = () => {
        // При очистке сохраняем default значения, включая conversionMode
        const defaults: JsonEngineInput = {}
        const uiInputsForClear = Array.isArray(ui) ? ui : undefined
        const todayStrForClear = new Date().toISOString().slice(0, 10)
        // Как и в getInitialValues, сначала резолвим unit_system, чтобы поля с
        // defaultBySystem сбрасывались на default для правильной системы единиц
        const unitSystemFieldConfigForClear = uiInputsForClear?.find((f: any) => f.name === 'unit_system')
        const unitSystemValueForClear =
            unitSystemFieldConfigForClear?.default !== undefined
                ? unitSystemFieldConfigForClear.default
                : config.inputs.find((i) => i.key === 'unit_system')?.default
        config.inputs.forEach((inp) => {
            const fieldConfig = uiInputsForClear?.find((f: any) => f.name === inp.key)
            const localeOverride = fieldConfig?.default
            let value: any
            if (fieldConfig?.defaultBySystem && unitSystemValueForClear !== undefined) {
                value = fieldConfig.defaultBySystem[String(unitSystemValueForClear)]
            } else {
                value = localeOverride !== undefined ? localeOverride : inp.default
            }
            if (fieldConfig?.type === 'date' && value === 'today') {
                value = todayStrForClear
            }
            if (value !== undefined) {
                defaults[inp.key] = value
            }
        })
        // Гарантируем, что conversionMode всегда есть
        if (defaults.conversionMode === undefined) {
            defaults.conversionMode = 'words'
        }
        setValues(defaults)
        setResult({})
    }

    // Форматирует одно значение output с учетом precision и unit
    // out.unitFrom позволяет брать единицу измерения динамически из значения другого поля
    // (например, из выбора валюты USD/EUR), а не из статичной строки out.unit
    const formatOutputValue = (out: any): string | null => {
        const raw = result[out.name]
        if (raw === undefined || raw === null) return null

        let formatted: string
        if (typeof raw === 'number') {
            const precision = out.precision !== undefined ? out.precision : 2
            const fixed = raw.toFixed(precision)
            // Если дробная часть после округления состоит из одних нулей дальше 2 знаков
            // (например "1000.000000"), обрезаем до 2 знаков ("1000.00") вместо того, чтобы
            // всегда показывать полную заданную точность
            if (precision > 2 && fixed.includes('.')) {
                const [intPart, decPart] = fixed.split('.')
                const trimmed = decPart.replace(/0+$/, '')
                formatted = trimmed.length >= 2 ? `${intPart}.${trimmed}` : `${intPart}.${decPart.slice(0, 2)}`
            } else {
                formatted = fixed
            }
        } else {
            formatted = String(raw)
        }

        let unit = out.unitFrom ? values[out.unitFrom] : out.unit
        // Для полей вроде "to_unit" в конвертерах единиц измерения показываем короткое
        // отображаемое сокращение (option.abbr), а не сырое внутреннее значение опции
        // (например "gallon_us_fluid") - если сокращение не задано (как у валютных
        // селектов '$'/'€'), используем значение как раньше
        if (out.unitFrom) {
            const uiInputs = Array.isArray(ui) ? ui : undefined
            const sourceField = uiInputs?.find((f: any) => f.name === out.unitFrom)
            const matchedOption = sourceField?.options?.find((o: any) => o.value === values[out.unitFrom])
            if (matchedOption?.abbr) {
                unit = matchedOption.abbr
            }
        }
        return `${formatted}${unit ? ` ${unit}` : ''}`
    }

    // Копирование результата - только видимый текст
    const handleCopy = () => {
        if (Object.keys(result).length === 0) return

        // Для калькуляторов с несколькими результатами копируем все строки с подписями
        // (подписи нужны, чтобы отличить значения друг от друга). Если результат один,
        // подпись поля - служебный текст и в буфер обмена не должна попадать - копируем
        // только само значение.
        const resultText = visibleOutputsConfig.length > 1
            ? visibleOutputsConfig
                .map((out) => {
                    const value = formatOutputValue(out)
                    return value ? `${out.label || out.name}: ${value}` : null
                })
                .filter(Boolean)
                .join('\n')
            : visibleOutputsConfig.length === 1
                ? (formatOutputValue(visibleOutputsConfig[0]) || '')
                : (result.result ? String(result.result) : (result.textResult ? String(result.textResult) : ''))

        if (resultText) {
            navigator.clipboard.writeText(resultText).then(() => {
                setCopied(true)
                setTimeout(() => setCopied(false), 2000)
            })
        }
    }

    // Установка PWA
    const handleInstall = async () => {
        console.log('handleInstall called, deferredPrompt:', !!deferredPrompt, 'isInstallable:', isInstallable)
        
        if (!deferredPrompt) {
            // Если prompt недоступен, перенаправляем на popup страницу, где можно установить PWA
            if (typeof window !== 'undefined') {
                console.log('PWA install prompt not available')
                
                // Проверяем, не в popup ли мы уже
                const isPopup = window.location.search.includes('do=pop')
                
                if (!isPopup) {
                    // Если не в popup, открываем popup страницу
                    const popupUrl = toolSlug 
                        ? `/${lang}/${toolSlug}?do=pop`
                        : `/${lang}?do=pop`
                    
                    // Отслеживание попытки установки без доступного prompt
                    if ((window as any).dataLayer) {
                        (window as any).dataLayer.push({
                            event: 'pwa_install_attempt',
                            eventCategory: 'Widget',
                            eventAction: 'PWA Install Attempt',
                            eventLabel: 'Redirecting to Popup',
                            widgetToolId: toolId || 'unknown',
                            widgetH1: h1 || 'Unknown',
                            widgetH1En: h1En || h1 || 'Unknown',
                            widgetLang: lang
                        })
                    }
                    
                    // Открываем в новом окне для установки PWA
                    window.open(popupUrl, '_blank', 'width=400,height=600')
                } else {
                    // Уже в popup, показываем инструкции
                    console.log('Already in popup mode, PWA install prompt not available')
                    // Можно показать сообщение о том, что установка недоступна или инструкции
                    // Но лучше просто ничего не делать, так как браузер сам покажет prompt, если возможно
                }
            }
            return
        }

        try {
            console.log('Showing PWA install prompt')
            // Показываем prompt установки
            deferredPrompt.prompt()

            // Ждем ответа пользователя
            const { outcome } = await deferredPrompt.userChoice
            console.log('User choice:', outcome)

            // Отслеживание в Google Analytics
            if (typeof window !== 'undefined' && (window as any).dataLayer) {
                (window as any).dataLayer.push({
                    event: 'pwa_install_prompt',
                    eventCategory: 'Widget',
                    eventAction: 'PWA Install Prompt',
                    eventLabel: outcome === 'accepted' ? 'Installed' : 'Dismissed',
                    widgetToolId: toolId || 'unknown',
                    widgetH1: h1 || 'Unknown',
                    widgetH1En: h1En || h1 || 'Unknown',
                    widgetLang: lang
                })
            }

            // Очищаем deferredPrompt после использования
            setDeferredPrompt(null)
            setIsInstallable(false)
        } catch (error) {
            console.error('Error during PWA installation:', error)
            // Если произошла ошибка, перенаправляем на popup страницу
            if (typeof window !== 'undefined') {
                const popupUrl = toolSlug 
                    ? `/${lang}/${toolSlug}?do=pop`
                    : `/${lang}?do=pop`
                window.open(popupUrl, '_blank', 'width=400,height=600')
            }
        }
    }

    // Получаем текущий режим конвертации из state
    // Теперь conversionMode ВСЕГДА есть в values, поэтому просто берем его напрямую
    const conversionMode = String(values.conversionMode || 'words')
    
    // Получаем конфигурацию полей из inputs_json
    // ui - это массив [{name, label, type, unit, min, max, options, placeholder}, ...] из tool_i18n.inputs_json
    const getFieldConfig = (key: string) => {
        if (Array.isArray(ui)) {
            return ui.find((f: any) => f.name === key)
        }
        if (Array.isArray(ui?.inputs)) {
            return ui.inputs.find((f: any) => f.name === key)
        }
        return ui?.inputs?.[key]
    }

    // outputs_json из tool_i18n: [{name, label, unit, precision}] - для калькуляторов с несколькими результатами
    const outputsConfig: any[] = Array.isArray(outputs) ? outputs : []
    const hasOutputsConfig = outputsConfig.length > 0
    // "steps" - зарезервированное имя output-поля: пошаговое решение рендерится в
    // отдельном блоке под результатом (см. ниже), а не в обычном списке результатов
    const visibleOutputsConfig = outputsConfig.filter((out) => out.name !== 'steps')
    const stepsOutputConfig = outputsConfig.find((out) => out.name === 'steps')

    const inputNumberConfig = getFieldConfig('inputNumber')
    const textConfig = getFieldConfig('text') // Для text case converter
    const modeConfig = getFieldConfig('mode') // Для text case converter
    const currencyConfig = getFieldConfig('currency')
    const vatRateConfig = getFieldConfig('vatRate')
    const textCaseConfig = getFieldConfig('textCase')

    // Валюты для dropdown
    const currencies = currencyConfig?.options || [
        { value: 'USD', label: 'USD' },
        { value: 'GBP', label: 'GBP' },
        { value: 'EUR', label: 'EUR' },
        { value: 'PLN', label: 'PLN' },
        { value: 'RUB', label: 'RUB' }
    ]

    // Опции для Letter Case
    const textCaseOptions = textCaseConfig?.options || [
        { value: 'lowercase', label: translations.lowercaseOption },
        { value: 'UPPERCASE', label: translations.uppercaseOption },
        { value: 'Title Case', label: translations.titleCaseOption },
        { value: 'Sentence case', label: translations.sentenceCaseOption }
    ]

    // Опции для Mode (text case converter)
    const modeOptions = modeConfig?.options || []

    // Определяем, это text case converter или number-to-words
    const isTextCaseConverter =
        config.inputs.some((inp: any) => inp.key === 'text') && config.inputs.some((inp: any) => inp.key === 'mode')
    const isNumberToWords = config.inputs.some((inp: any) => inp.key === 'inputNumber')

    // Получаем текст результата для отображения
    const resultText = result.result ? String(result.result) : (result.textResult ? String(result.textResult) : '')

    return (
        <div className="w-full lg:w-[420px] lg:float-left lg:mr-5 mb-5 relative mx-auto lg:mx-0" style={{ maxWidth: '420px' }}>
            {/* Header Виджета */}
            <div className="bg-blue-600 text-white px-5 py-3 rounded-t-lg mx-auto" style={{ width: '100%', maxWidth: '400px' }}>
                <h2 className="text-lg font-bold">{h1}</h2>
            </div>

            {/* Тело Калькулятора */}
            <div className="bg-white border border-gray-200 border-t-0 p-5 mx-auto relative" style={{ width: '100%', maxWidth: '400px' }}>
                {/* Универсальное поле ввода - поддерживает text, textarea, select */}
                {isTextCaseConverter ? (
                    <>
                        {/* Поле ввода текста (textarea для text case converter) */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {textConfig?.label || 'Add text:'}
                            </label>
                            <textarea
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none resize-y"
                                rows={4}
                                placeholder={textConfig?.placeholder || 'Enter your text here...'}
                                onChange={(e) => handleChange('text', e.target.value)}
                                value={values.text !== undefined ? String(values.text) : ''}
                            />
                        </div>
                        {/* Выбор режима регистра */}
                        {modeConfig && (
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {modeConfig.label || 'To format:'}
                                </label>
                                <select
                                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                                    aria-label={modeConfig.label || 'To format:'}
                                    onChange={(e) => handleChange('mode', e.target.value)}
                                    value={values.mode !== undefined ? String(values.mode) : (config.inputs.find((i: any) => i.key === 'mode')?.default || 'lower')}
                                >
                                    {modeOptions.map((option: any) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </>
                ) : isNumberToWords ? (
                    <>
                        {/* Поле ввода числа (для number-to-words) */}
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                {translations.inputLabel}
                            </label>
                            <input
                                type="text"
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                                placeholder={inputNumberConfig?.placeholder || translations.inputPlaceholder}
                                onChange={(e) => handleChange('inputNumber', e.target.value)}
                                value={values.inputNumber !== undefined ? String(values.inputNumber) : ''}
                            />
                        </div>
                    </>
                ) : (
                    /* Универсальный рендер полей ввода по config.inputs + inputs_json (для всех остальных калькуляторов) */
                    <>
                        {config.inputs.map((inp) => {
                            const fieldConfig = getFieldConfig(inp.key)
                            // hidden: true - поле участвует в расчёте (со своим default'ом), но не
                            // показывается в UI - например, unit_system, зафиксированный на 'metric'
                            // для локалей без переключателя единиц измерения
                            if (fieldConfig?.hidden) return null
                            const label = fieldConfig?.label || inp.key
                            // unitFrom + unitMap - динамическая единица измерения поля ввода,
                            // зависящая от значения другого поля (например, лейбл "Length (ft)"
                            // меняется на "Length (m)" при переключении unit_system) - в отличие
                            // от unitFrom у outputs (который берёт option.abbr), здесь у каждого
                            // поля свой unitMap, т.к. разные величины (длина, глубина) используют
                            // разные единицы для одного и того же значения unit_system
                            const unit = fieldConfig?.unitFrom
                                ? fieldConfig?.unitMap?.[String(values[fieldConfig.unitFrom])]
                                : fieldConfig?.unit
                            const isSelect = fieldConfig?.type === 'select' && Array.isArray(fieldConfig?.options)
                            const isDate = fieldConfig?.type === 'date'
                            const isText = fieldConfig?.type === 'text'
                            const isTextarea = fieldConfig?.type === 'textarea'
                            const currentValue = values[inp.key] !== undefined ? values[inp.key] : (inp.default ?? '')

                            return (
                                <div className="mb-4" key={inp.key}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {label}{unit ? ` (${unit})` : ''}
                                    </label>
                                    {isSelect ? (
                                        <select
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                                            aria-label={`${label}${unit ? ` (${unit})` : ''}`}
                                            onChange={(e) => handleChange(inp.key, e.target.value)}
                                            value={String(currentValue)}
                                        >
                                            {fieldConfig.options.map((option: any) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    ) : isDate ? (
                                        <input
                                            type="date"
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                                            min={fieldConfig?.min ?? undefined}
                                            max={fieldConfig?.max ?? undefined}
                                            onChange={(e) => handleChange(inp.key, e.target.value)}
                                            value={String(currentValue)}
                                        />
                                    ) : isText ? (
                                        <input
                                            type="text"
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                                            placeholder={fieldConfig?.placeholder || ''}
                                            onChange={(e) => handleChange(inp.key, e.target.value)}
                                            value={String(currentValue)}
                                        />
                                    ) : isTextarea ? (
                                        <textarea
                                            rows={5}
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                                            placeholder={fieldConfig?.placeholder || ''}
                                            onChange={(e) => handleChange(inp.key, e.target.value)}
                                            value={String(currentValue)}
                                        />
                                    ) : (
                                        <input
                                            type="number"
                                            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                                            placeholder={fieldConfig?.placeholder || ''}
                                            min={fieldConfig?.min ?? undefined}
                                            max={fieldConfig?.max ?? undefined}
                                            onChange={(e) => handleChange(inp.key, e.target.value)}
                                            value={String(currentValue)}
                                        />
                                    )}
                                    {fieldConfig?.description && (
                                        <p className="text-xs text-gray-500 mt-1">{fieldConfig.description}</p>
                                    )}
                                </div>
                            )
                        })}
                    </>
                )}

                {/* Кнопки-переключатели для выбора формата (только для number-to-words) */}
                {isNumberToWords && (
                    <>
                        {/* Заголовок "To:" */}
                        <div className="mt-[15px] mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                                {translations.formatLabel}
                            </label>
                        </div>

                        <div className="space-y-[15px] mb-4">
                    {/* Words */}
                    <div className="flex items-center">
                        <button
                            type="button"
                            onClick={() => handleRadioChange('words')}
                            className={`mr-2 px-3 py-1.5 text-sm font-medium rounded-md border transition-colors cursor-pointer ${
                                conversionMode === 'words'
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            {translations.wordsOption}
                        </button>
                    </div>

                    {/* Currency */}
                    <div className="flex items-center flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={() => handleRadioChange('currency')}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md border transition-colors cursor-pointer ${
                                conversionMode === 'currency'
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            {translations.currencyOption}
                        </button>
                        <select
                            className="border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                            style={{ width: '65px', height: '29px', paddingLeft: '8px', paddingRight: '20px', appearance: 'none', backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23333\' d=\'M6 9L1 4h10z\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 6px center' }}
                            aria-label={translations.currencyOption}
                            onChange={(e) => handleChange('currency', e.target.value)}
                            value={values.currency !== undefined ? String(values.currency) : (config.inputs.find(i => i.key === 'currency')?.default || 'USD')}
                        >
                            {currencies.map((curr: any) => (
                                <option key={curr.value} value={curr.value}>
                                    {curr.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Currency + VAT */}
                    <div className="flex items-center flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={() => handleRadioChange('currency_vat')}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md border transition-colors cursor-pointer ${
                                conversionMode === 'currency_vat'
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            {translations.currencyOption}
                        </button>
                        <select
                            className="border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                            style={{ width: '65px', height: '29px', paddingLeft: '8px', paddingRight: '20px', appearance: 'none', backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23333\' d=\'M6 9L1 4h10z\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 6px center' }}
                            aria-label={translations.currencyOption}
                            onChange={(e) => handleChange('currency', e.target.value)}
                            value={values.currency !== undefined ? String(values.currency) : (config.inputs.find(i => i.key === 'currency')?.default || 'USD')}
                        >
                            {currencies.map((curr: any) => (
                                <option key={curr.value} value={curr.value}>
                                    {curr.label}
                                </option>
                            ))}
                        </select>
                        <span className="text-sm text-gray-700">{translations.plusVat}</span>
                        <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]{1,2}"
                            maxLength={2}
                            className="border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none text-right pr-6"
                            style={{ width: '65px', height: '29px', paddingLeft: '8px', backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Ctext x=\'6\' y=\'9\' text-anchor=\'middle\' font-size=\'10\' fill=\'%23333\'%3E%25%3C/text%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 6px center' }}
                            placeholder="0"
                            onChange={(e) => {
                                const val = e.target.value.replace(/[^0-9]/g, '')
                                if (val === '' || (parseInt(val) >= 0 && parseInt(val) <= 100)) {
                                    handleChange('vatRate', val === '' ? 0 : parseInt(val))
                                }
                            }}
                            value={values.vatRate !== undefined && values.vatRate !== 0 ? String(values.vatRate) : ''}
                        />
                    </div>

                    {/* Check Writing */}
                    <div className="flex items-center">
                        <button
                            type="button"
                            onClick={() => handleRadioChange('check_writing')}
                            className={`px-3 py-1.5 text-sm font-medium rounded-md border transition-colors cursor-pointer ${
                                conversionMode === 'check_writing'
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                            {translations.checkWritingOption}
                        </button>
                    </div>
                </div>

                        {/* Letter Case (только для number-to-words) */}
                        {isNumberToWords && (
                            <div className="flex items-center mb-4">
                                <label className="text-sm font-medium text-gray-700 mr-[15px]">
                                    {translations.letterCaseLabel}
                                </label>
                                <select
                                    className="border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
                                    style={{ width: '180px', height: '29px', paddingLeft: '8px', paddingRight: '20px', appearance: 'none', backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23333\' d=\'M6 9L1 4h10z\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 6px center' }}
                                    aria-label={translations.letterCaseLabel}
                                    onChange={(e) => handleChange('textCase', e.target.value)}
                                    value={values.textCase !== undefined ? String(values.textCase) : (config.inputs.find((i: any) => i.key === 'textCase')?.default || 'Sentence case')}
                                >
                                    {textCaseOptions.map((opt: any) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </>
                )}

                {/* Action Row */}
                <div className="flex gap-2 mb-4">
                    <button
                        onClick={handleClear}
                        className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-md transition-colors"
                    >
                        {translations.clear}
                    </button>
                    <button
                        onClick={handleCalculate}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition-colors"
                    >
                        {translations.calculate}
                    </button>
                </div>

                {/* Result Box */}
                <div
                    className="border border-gray-300 rounded-md relative mx-auto"
                    style={{ width: '370px', maxWidth: '100%', minHeight: '150px' }}
                >
                    {/* Top Row: Label and Copy Button */}
                    <div className="flex justify-between items-center p-2 border-b border-gray-300 bg-gray-50">
                        <span className="text-sm font-medium text-gray-700">{translations.result}</span>
                        <button
                            onClick={handleCopy}
                            className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded transition-colors"
                            disabled={hasOutputsConfig ? Object.keys(result).length === 0 : !resultText}
                        >
                            {copied ? '✓' : translations.copy}
                        </button>
                    </div>

                    {/* Result Content */}
                    <div className="p-4" style={{ paddingBottom: '20px' }}>
                        {visibleOutputsConfig.length > 1 ? (
                            Object.keys(result).length > 0 ? (
                                <div className="space-y-2">
                                    {visibleOutputsConfig.map((out) => {
                                        const value = formatOutputValue(out)
                                        if (value === null) return null
                                        return (
                                            <div key={out.name} className="flex justify-between gap-3 text-gray-800">
                                                <span className="text-sm text-gray-600 shrink-0">{out.label || out.name}</span>
                                                <span className="font-semibold text-right min-w-0 break-all">{value}</span>
                                            </div>
                                        )
                                    })}
                                </div>
                            ) : (
                                <div className="text-gray-400 text-sm">—</div>
                            )
                        ) : visibleOutputsConfig.length === 1 ? (
                            (() => {
                                const value = formatOutputValue(visibleOutputsConfig[0])
                                return value ? (
                                    <div className="text-gray-800 font-semibold whitespace-pre-wrap break-all">{value}</div>
                                ) : (
                                    <div className="text-gray-400 text-sm">—</div>
                                )
                            })()
                        ) : resultText ? (
                            <div className="text-gray-800 whitespace-pre-wrap break-all">
                                {resultText}
                            </div>
                        ) : (
                            <div className="text-gray-400 text-sm">—</div>
                        )}
                    </div>

                    {/* Optional step-by-step solution box: any tool whose outputs include a
                        field named "steps" gets its (newline-separated) value rendered here
                        as a numbered list, instead of being squeezed into the result row above */}
                    {stepsOutputConfig && typeof result.steps === 'string' && result.steps.trim() ? (
                        <div className="border-t border-gray-300">
                            <div className="p-2 border-b border-gray-300 bg-gray-50">
                                <span className="text-sm font-medium text-gray-700">{STEP_BY_STEP_LABEL[lang] || STEP_BY_STEP_LABEL.en}</span>
                            </div>
                            <ol className="p-4 space-y-1.5 list-decimal list-inside text-sm text-gray-700">
                                {result.steps.split('\n').filter((line: string) => line.trim()).map((line: string, i: number) => (
                                    <li key={i} className="break-words">{line}</li>
                                ))}
                            </ol>
                        </div>
                    ) : null}
                </div>

                {/* Optional inline SVG chart: trig calculators (sine/cosine/tangent wave, unit
                    circle), chemistry calculators (element composition bar chart, ideal
                    gas law P-V isotherm, pH scale), and kinematics calculators (displacement/
                    velocity-time graphs, projectile trajectory) */}
                {(() => {
                    const toolIdStr = String(toolId)
                    const waveFunc = TRIG_WAVE_CHART_TOOL_IDS[toolIdStr]
                    const isUnitCircle = toolIdStr === UNIT_CIRCLE_CHART_TOOL_ID
                    const isCompositionChart = CHEMISTRY_COMPOSITION_CHART_TOOL_IDS.includes(toolIdStr)
                    const isGasLawChart = toolIdStr === IDEAL_GAS_LAW_CHART_TOOL_ID
                    const isPhChart = toolIdStr === PH_CHART_TOOL_ID
                    const isDisplacementChart = toolIdStr === DISPLACEMENT_TIME_CHART_TOOL_ID
                    const isFreeFallChart = toolIdStr === FREE_FALL_CHART_TOOL_ID
                    const isProjectileChart = toolIdStr === PROJECTILE_CHART_TOOL_ID
                    const isVelocityTimeChart = toolIdStr === VELOCITY_TIME_CHART_TOOL_ID
                    const isImpulseChart = toolIdStr === IMPULSE_CHART_TOOL_ID
                    const isInclineChart = toolIdStr === INCLINE_CHART_TOOL_ID
                    const isWorkChart = toolIdStr === WORK_CHART_TOOL_ID
                    const isSpringForceChart = toolIdStr === SPRING_FORCE_CHART_TOOL_ID
                    const isMechanicalEnergyChart = toolIdStr === MECHANICAL_ENERGY_CHART_TOOL_ID
                    const isHeatEnergyChart = toolIdStr === HEAT_ENERGY_CHART_TOOL_ID
                    const isEscapeVelocityChart = toolIdStr === ESCAPE_VELOCITY_CHART_TOOL_ID
                    const isWindPowerChart = toolIdStr === WIND_POWER_CHART_TOOL_ID
                    const isWaveSpeedChart = toolIdStr === WAVE_SPEED_CHART_TOOL_ID
                    const isStandingWaveChart = toolIdStr === STANDING_WAVE_CHART_TOOL_ID
                    const isBuoyancyChart = toolIdStr === BUOYANCY_CHART_TOOL_ID
                    const isBoyleLawChart = toolIdStr === BOYLE_LAW_CHART_TOOL_ID
                    const solidGeometryShape = SOLID_GEOMETRY_CHART_TOOL_IDS[toolIdStr]
                    const planeGeometryShape = PLANE_GEOMETRY_CHART_TOOL_IDS[toolIdStr]
                    if (
                        !waveFunc && !isUnitCircle && !isCompositionChart && !isGasLawChart && !isPhChart &&
                        !isDisplacementChart && !isFreeFallChart && !isProjectileChart && !isVelocityTimeChart &&
                        !isImpulseChart && !isInclineChart && !isWorkChart && !isSpringForceChart &&
                        !isMechanicalEnergyChart && !isHeatEnergyChart && !isEscapeVelocityChart && !isWindPowerChart &&
                        !isWaveSpeedChart && !isStandingWaveChart && !isBuoyancyChart && !isBoyleLawChart &&
                        !solidGeometryShape && !planeGeometryShape
                    ) return null

                    if (solidGeometryShape) {
                        return (
                            <div className="flex justify-center mt-4 mb-2">
                                {solidGeometryShape === 'cube' && <CubeChart side={Number(values.side_a) || 0} />}
                                {solidGeometryShape === 'rectangularPrism' && (
                                    <RectangularPrismChart length={Number(values.length) || 0} width={Number(values.width) || 0} height={Number(values.height) || 0} />
                                )}
                                {solidGeometryShape === 'cylinder' && <CylinderChart radius={Number(values.radius) || 0} height={Number(values.height) || 0} />}
                                {solidGeometryShape === 'cone' && <ConeChart radius={Number(values.radius) || 0} height={Number(values.height) || 0} />}
                                {solidGeometryShape === 'sphere' && <SphereChart radius={Number(values.radius) || 0} />}
                                {solidGeometryShape === 'squarePyramid' && (
                                    <SquarePyramidChart baseSide={Number(values.base_side) || 0} height={Number(values.height) || 0} />
                                )}
                                {solidGeometryShape === 'triangularPrism' && (
                                    <TriangularPrismChart base={Number(values.base) || 0} triangleHeight={Number(values.triangle_height) || 0} length={Number(values.length) || 0} />
                                )}
                                {solidGeometryShape === 'hemisphere' && <HemisphereChart radius={Number(values.radius) || 0} />}
                                {solidGeometryShape === 'coneFrustum' && (
                                    <ConeFrustumChart topRadius={Number(values.top_radius) || 0} bottomRadius={Number(values.bottom_radius) || 0} height={Number(values.height) || 0} />
                                )}
                                {solidGeometryShape === 'ellipsoid' && (
                                    <EllipsoidChart semiAxisA={Number(values.semi_axis_a) || 0} semiAxisB={Number(values.semi_axis_b) || 0} semiAxisC={Number(values.semi_axis_c) || 0} />
                                )}
                                {solidGeometryShape === 'capsule' && (
                                    <CapsuleChart radius={Number(values.radius) || 0} cylinderHeight={Number(values.cylinder_height) || 0} />
                                )}
                                {solidGeometryShape === 'torus' && (
                                    <TorusChart tubeRadius={Number(values.tube_radius) || 0} centerRadius={Number(values.center_radius) || 0} />
                                )}
                            </div>
                        )
                    }

                    if (planeGeometryShape) {
                        return (
                            <div className="flex justify-center mt-4 mb-2">
                                {planeGeometryShape === 'triangle' && (
                                    <TriangleChart sideA={Number(values.side_a) || 0} sideB={Number(values.side_b) || 0} sideC={Number(values.side_c) || 0} />
                                )}
                                {planeGeometryShape === 'rightTriangle' && (() => {
                                    const solveFor = String(values.solve_for || 'hypotenuse_c')
                                    const legA = solveFor === 'leg_a' ? Number(result.result) || 0 : Number(values.leg_a) || 0
                                    const legB = solveFor === 'leg_b' ? Number(result.result) || 0 : Number(values.leg_b) || 0
                                    const hypotenuseC = solveFor === 'hypotenuse_c' ? Number(result.result) || 0 : Number(values.hypotenuse_c) || 0
                                    return <RightTriangleChart legA={legA} legB={legB} hypotenuseC={hypotenuseC} />
                                })()}
                                {planeGeometryShape === 'circle' && <CircleChart radius={Number(values.radius) || 0} />}
                                {planeGeometryShape === 'circleSector' && (
                                    <CircleSectorChart radius={Number(values.radius) || 0} angleDeg={Number(values.angle_deg) || 0} />
                                )}
                                {planeGeometryShape === 'rectangle' && (
                                    <RectangleChart length={Number(values.length) || 0} width={Number(values.width) || 0} />
                                )}
                                {planeGeometryShape === 'square' && <SquareChart side={Number(values.side_a) || 0} />}
                                {planeGeometryShape === 'parallelogram' && (
                                    <ParallelogramChart base={Number(values.base) || 0} height={Number(values.height) || 0} sideB={Number(values.side_b) || 0} />
                                )}
                                {planeGeometryShape === 'trapezoid' && (
                                    <TrapezoidChart baseA={Number(values.base_a) || 0} baseB={Number(values.base_b) || 0} height={Number(values.height) || 0} />
                                )}
                                {planeGeometryShape === 'rhombus' && (
                                    <RhombusChart diagonalP={Number(values.diagonal_p) || 0} diagonalQ={Number(values.diagonal_q) || 0} />
                                )}
                                {planeGeometryShape === 'regularPolygon' && (
                                    <RegularPolygonChart numSides={Number(values.num_sides) || 3} sideLength={Number(values.side_length) || 0} />
                                )}
                                {planeGeometryShape === 'ellipse' && (
                                    <EllipseChart semiAxisA={Number(values.semi_axis_a) || 0} semiAxisB={Number(values.semi_axis_b) || 0} />
                                )}
                                {planeGeometryShape === 'kite' && (
                                    <KiteChart
                                        diagonalP={Number(values.diagonal_p) || 0}
                                        diagonalQ={Number(values.diagonal_q) || 0}
                                        sideA={Number(values.side_a) || 0}
                                        sideB={Number(values.side_b) || 0}
                                    />
                                )}
                            </div>
                        )
                    }

                    if (isMechanicalEnergyChart) {
                        return (
                            <div className="flex justify-center mt-4 mb-2">
                                <EnergyConservationChart
                                    mass={Number(values.mass_kg) || 0}
                                    heightMax={Number(values.height_m) || 0}
                                    totalEnergy={Number(result.mechanical_energy_j) || 0}
                                    gravity={Number(values.gravity_ms2) || 9.81}
                                />
                            </div>
                        )
                    }
                    if (isHeatEnergyChart) {
                        const solveFor = String(values.solve_for || 'heat')
                        const mass = solveFor === 'mass' ? Number(result.result) || 0 : Number(values.mass_kg) || 0
                        const specificHeat = solveFor === 'specific_heat' ? Number(result.result) || 0 : Number(values.specific_heat_jkgc) || 0
                        const deltaT = solveFor === 'delta_t' ? Number(result.result) || 0 : Number(values.delta_t_c) || 0
                        return (
                            <div className="flex justify-center mt-4 mb-2">
                                <HeatTemperatureChart mass={mass} specificHeat={specificHeat} deltaT={deltaT} />
                            </div>
                        )
                    }
                    if (isEscapeVelocityChart) {
                        return (
                            <div className="flex justify-center mt-4 mb-2">
                                <EscapeVelocityChart mass={Number(values.mass_kg) || 0} radius={Number(values.radius_m) || 1} />
                            </div>
                        )
                    }
                    if (isWindPowerChart) {
                        return (
                            <div className="flex justify-center mt-4 mb-2">
                                <WindPowerChart
                                    airDensity={Number(values.air_density_kgm3) || 0}
                                    sweptArea={Number(values.swept_area_m2) || 0}
                                    windSpeed={Number(values.wind_speed_ms) || 0}
                                />
                            </div>
                        )
                    }
                    if (isWaveSpeedChart) {
                        const solveFor = String(values.solve_for || 'wave_speed')
                        const wavelength = solveFor === 'wavelength' ? Number(result.result) || 0 : Number(values.wavelength_m) || 0
                        return (
                            <div className="flex justify-center mt-4 mb-2">
                                <WaveformChart wavelengthM={wavelength} wavelengthLabel={`λ = ${wavelength.toFixed(4)} m`} />
                            </div>
                        )
                    }
                    if (isStandingWaveChart) {
                        return (
                            <div className="flex justify-center mt-4 mb-2">
                                <StandingWaveChart harmonicNumber={Number(values.harmonic_number) || 1} />
                            </div>
                        )
                    }
                    if (isBuoyancyChart) {
                        return (
                            <div className="flex justify-center mt-4 mb-2">
                                <BuoyancyChart
                                    buoyantForceN={Number(result.buoyant_force_n) || 0}
                                    weightN={Number(result.object_weight_n) || 0}
                                    buoyantLabel="F_b"
                                    weightLabel="W"
                                />
                            </div>
                        )
                    }
                    if (isBoyleLawChart) {
                        const solveFor = String(values.solve_for || 'pressure2')
                        const p1 = Number(values.pressure1) || 0
                        const v1 = Number(values.volume1) || 0
                        const p2 = solveFor === 'pressure2' ? Number(result.result) || 0 : Number(values.pressure2) || 0
                        const v2 = solveFor === 'volume2' ? Number(result.result) || 0 : Number(values.volume2) || 0
                        return (
                            <div className="flex justify-center mt-4 mb-2">
                                <BoyleLawChart pressure1={p1} volume1={v1} pressure2={p2} volume2={v2} />
                            </div>
                        )
                    }
                    if (isImpulseChart) {
                        return (
                            <div className="flex justify-center mt-4 mb-2">
                                <ForceAreaChart force={Number(result.avg_force_n) || 0} extent={Number(values.time_s) || 0} xAxisLabel="Time (s)" />
                            </div>
                        )
                    }
                    if (isInclineChart) {
                        return (
                            <div className="flex justify-center mt-4 mb-2">
                                <InclineDiagramChart angleDeg={Number(values.angle_deg) || 0} />
                            </div>
                        )
                    }
                    if (isWorkChart) {
                        return (
                            <div className="flex justify-center mt-4 mb-2">
                                <ForceAreaChart force={Number(values.force_n) || 0} extent={Number(values.distance_m) || 0} xAxisLabel="Distance (m)" />
                            </div>
                        )
                    }
                    if (isSpringForceChart) {
                        const solveFor = String(values.solve_for || 'force')
                        const k = solveFor === 'spring_constant' ? Number(result.result) || 0 : Number(values.spring_constant_nm) || 0
                        const x = solveFor === 'displacement' ? Number(result.result) || 0 : Number(values.displacement_m) || 0
                        return (
                            <div className="flex justify-center mt-4 mb-2">
                                <SpringForceChart springConstant={k} displacement={x} />
                            </div>
                        )
                    }
                    if (isDisplacementChart) {
                        return (
                            <div className="flex justify-center mt-4 mb-2">
                                <DisplacementTimeChart
                                    initialVelocity={Number(values.initial_velocity) || 0}
                                    acceleration={Number(values.acceleration) || 0}
                                    timeS={Number(values.time_s) || 0}
                                />
                            </div>
                        )
                    }
                    if (isFreeFallChart) {
                        return (
                            <div className="flex justify-center mt-4 mb-2">
                                <DisplacementTimeChart
                                    initialVelocity={0}
                                    acceleration={9.81}
                                    timeS={Number(result.time_s) || 0}
                                />
                            </div>
                        )
                    }
                    if (isProjectileChart) {
                        return (
                            <div className="flex justify-center mt-4 mb-2">
                                <ProjectileTrajectoryChart
                                    speedMs={Number(values.initial_speed_ms) || 0}
                                    angleDeg={Number(values.launch_angle_deg) || 0}
                                    initialHeightM={Number(values.initial_height_m) || 0}
                                />
                            </div>
                        )
                    }
                    if (isVelocityTimeChart) {
                        return (
                            <div className="flex justify-center mt-4 mb-2">
                                <VelocityTimeChart
                                    initialVelocity={Number(result.u) || 0}
                                    acceleration={Number(result.a) || 0}
                                    timeS={Number(result.t) || 0}
                                />
                            </div>
                        )
                    }
                    if (isCompositionChart) {
                        return (
                            <div className="flex justify-center mt-4 mb-2">
                                <ElementCompositionChart formula={String(values.formula || '')} />
                            </div>
                        )
                    }
                    if (isGasLawChart) {
                        const solveFor = String(values.solve_for || 'p')
                        const p = solveFor === 'p' ? Number(result.result) || 0 : Number(values.pressure_atm) || 0
                        const v = solveFor === 'v' ? Number(result.result) || 0 : Number(values.volume_liters) || 0
                        const n = solveFor === 'n' ? Number(result.result) || 0 : Number(values.moles) || 0
                        const tK = solveFor === 't' ? Number(result.result) || 0 : Number(values.temperature_k) || 0
                        return (
                            <div className="flex justify-center mt-4 mb-2">
                                <IdealGasLawChart pressureAtm={p} volumeLiters={v} moles={n} temperatureK={tK} />
                            </div>
                        )
                    }
                    if (isPhChart) {
                        return (
                            <div className="flex justify-center mt-4 mb-2">
                                <PhScaleChart ph={Number(result.ph) || 0} />
                            </div>
                        )
                    }

                    const rawAngle = Number(values.angle_value) || 0
                    const angleUnit = String(values.angle_unit || 'degree')
                    const angleDegrees = angleUnit === 'radian' ? (rawAngle * 180) / Math.PI : rawAngle
                    return (
                        <div className="flex justify-center mt-4 mb-2">
                            {waveFunc ? (
                                <TrigWaveChart func={waveFunc} angleDegrees={angleDegrees} />
                            ) : (
                                <UnitCircleChart angleDegrees={angleDegrees} />
                            )}
                        </div>
                    )
                })()}

                {/* Footer Links (hidden in popup/embed mode, only the logo stays) */}
                {!isPopup && (
                    <div className="mt-5 flex flex-col items-start gap-2">
                        <Link href={`/${lang}/contact`} className={WIDGET_FOOTER_BTN}>
                            <IconSuggest />
                            <span>{translations.suggest}</span>
                        </Link>
                        <button onClick={handleInstall} type="button" className={`${WIDGET_FOOTER_BTN} cursor-pointer`}>
                            <IconDownload />
                            <span>{translations.downloadWidget}</span>
                        </button>
                        <Link
                            href={toolSlug ? `/${lang}/widget/${toolSlug}` : (widgetPageSlug ? `/${lang}/${widgetPageSlug}` : `/${lang}/widget`)}
                            className={WIDGET_FOOTER_BTN}
                        >
                            <IconEmbed />
                            <span>{translations.getWidget}</span>
                        </Link>
                    </div>
                )}

                {/* Logo */}
                <div className="mt-5 text-right" style={{ marginTop: '20px' }}>
                    <Link 
                        href={`/${lang}`}
                        className="logo-widget inline-block"
                        onClick={() => {
                            // Отправка события в Google Analytics через dataLayer
                            if (typeof window !== 'undefined' && (window as any).dataLayer) {
                                (window as any).dataLayer.push({
                                    event: 'logo_click',
                                    eventCategory: 'Widget',
                                    eventAction: 'Logo Click',
                                    eventLabel: 'Logo Click',
                                    widgetToolId: toolId || 'unknown',
                                    widgetH1: h1 || 'Unknown',
                                    widgetH1En: h1En || h1 || 'Unknown',
                                    widgetLang: lang,
                                    widgetReferrer: document.referrer || window.location.href
                                })
                            }
                        }}
                    >
                        <Image
                            src="/calculatorscope-logo.svg"
                            alt="Calculator Scope"
                            width={90}
                            height={90}
                            className="object-contain inline-block"
                        />
                    </Link>
                </div>
            </div>
        </div>
    )
}
